/**
 * Co-parentage — Scolarité (port `vCpSchool` proto l. 2126-2146).
 *
 * Phase 1, incrément 18. Vue dédiée aux entrées scolaires (notes,
 * devoirs, infos) du foyer co-parentage. Route `/cp/school` —
 * accessible directement par URL (pas dans la nav, pour respecter
 * la limite de 5 onglets co-parentage de CLAUDE.md §2.1).
 *
 * Composition fidèle au proto :
 *   - Bouton + Ajouter une entrée (CTA primary plein largeur).
 *   - Liste sobre triée par date desc avec icône colorée par type
 *     (note=vert / devoir=orange / info=bleu), subject + teacher +
 *     date, comment optionnel, value optionnelle à droite.
 *   - Empty state avec icône 🎓.
 *   - Modal d'ajout : matière, type, valeur, enseignant, commentaire.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons, type IconName } from '../../ui/icons.js';
import styles from './SchoolView.module.css';
import { getLang, t } from '../../i18n/index.js';
import { formatIsoShort } from '../../lib/date.js';
import { addSchoolEntry, getCurrentHome, removeSchoolEntry } from '../../stores/homes.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import { modalTemplate } from '../../ui/Modal.js';
import type { CoparentingHomeData, SchoolEntry } from '@nestor/shared-types';
import { editorialHero } from '../../ui/Hero.js';

const MODAL_ADD = 'cp-school:add';

// Audit D2 : icônes filaires (plus d'emoji UI).
const TYPE_ICONS: Record<SchoolEntry['type'], IconName> = {
  note: 'receipt',
  devoir: 'pencil',
  info: 'book',
};

const TYPE_LABEL_KEYS: Record<SchoolEntry['type'], string> = {
  note: 'cp_school_type_note',
  devoir: 'cp_school_type_devoir',
  info: 'cp_school_type_info',
};

interface FormState {
  type: SchoolEntry['type'];
  subject: string;
  value: string;
  teacher: string;
  comment: string;
}

const formState: FormState = {
  type: 'note',
  subject: '',
  value: '',
  teacher: '',
  comment: '',
};

function typeIconClass(type: SchoolEntry['type']): string {
  switch (type) {
    case 'note':
      return `${styles.icon} ${styles.iconNote}`;
    case 'devoir':
      return `${styles.icon} ${styles.iconDevoir}`;
    case 'info':
      return `${styles.icon} ${styles.iconInfo}`;
  }
}

function typeValueClass(type: SchoolEntry['type']): string {
  switch (type) {
    case 'note':
      return `${styles.value} ${styles.valueNote}`;
    case 'devoir':
      return `${styles.value} ${styles.valueDevoir}`;
    case 'info':
      return `${styles.value} ${styles.valueInfo}`;
  }
}

function entryRowTemplate(entry: SchoolEntry, homeId: string): TemplateResult {
  const onRemove = (): void => {
    removeSchoolEntry(homeId, entry.id);
  };
  const metaParts: string[] = [];
  if (entry.teacher) metaParts.push(entry.teacher);
  metaParts.push(formatIsoShort(entry.date, getLang()));
  return html`<div class=${styles.row} data-testid="cp-school-row-${entry.id}">
    <div class=${typeIconClass(entry.type)}>${Icon(Icons[TYPE_ICONS[entry.type]], 16)}</div>
    <div class=${styles.main}>
      <div class=${styles.subject}>${entry.subject ?? '—'}</div>
      <div class=${styles.meta}>${metaParts.join(' · ')}</div>
      ${entry.comment ? html`<div class=${styles.comment}>${entry.comment}</div>` : html``}
    </div>
    ${entry.value ? html`<div class=${typeValueClass(entry.type)}>${entry.value}</div>` : html``}
    <button
      type="button"
      class=${styles.removeBtn}
      data-testid="cp-school-remove-${entry.id}"
      aria-label=${t('cp_school_remove')}
      @click=${onRemove}
    >
      ×
    </button>
  </div>`;
}

function modalContentTemplate(homeId: string): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    addSchoolEntry(homeId, {
      type: formState.type,
      subject: formState.subject,
      value: formState.value,
      teacher: formState.teacher,
      comment: formState.comment,
    });
    formState.subject = '';
    formState.value = '';
    formState.teacher = '';
    formState.comment = '';
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_school_label_subject')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-school-form-subject"
        placeholder=${t('cp_school_placeholder_subject')}
        .value=${formState.subject}
        @input=${(e: Event): void => {
          formState.subject = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroupRow}>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_school_label_type')}</label>
        <select
          class=${styles.formSelect}
          data-testid="cp-school-form-type"
          .value=${formState.type}
          @change=${(e: Event): void => {
            formState.type = (e.target as HTMLSelectElement).value as SchoolEntry['type'];
          }}
        >
          <option value="note">
            ${TYPE_ICONS.note} ${t(TYPE_LABEL_KEYS.note as Parameters<typeof t>[0])}
          </option>
          <option value="devoir">
            ${TYPE_ICONS.devoir} ${t(TYPE_LABEL_KEYS.devoir as Parameters<typeof t>[0])}
          </option>
          <option value="info">
            ${TYPE_ICONS.info} ${t(TYPE_LABEL_KEYS.info as Parameters<typeof t>[0])}
          </option>
        </select>
      </div>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_school_label_value')}</label>
        <input
          class=${styles.formInput}
          type="text"
          data-testid="cp-school-form-value"
          placeholder=${t('cp_school_placeholder_value')}
          .value=${formState.value}
          @input=${(e: Event): void => {
            formState.value = (e.target as HTMLInputElement).value;
          }}
        />
      </div>
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_school_label_teacher')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-school-form-teacher"
        placeholder=${t('cp_school_placeholder_teacher')}
        .value=${formState.teacher}
        @input=${(e: Event): void => {
          formState.teacher = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_school_label_comment')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-school-form-comment"
        placeholder=${t('cp_school_placeholder_comment')}
        .value=${formState.comment}
        @input=${(e: Event): void => {
          formState.comment = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    <button type="submit" class=${styles.formSubmit} data-testid="cp-school-form-submit">
      ${t('cp_school_btn_submit')}
    </button>
  </form>`;
}

export function renderCpSchoolView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    return html`<section class=${styles.view} data-testid="view-cp-school">
      <p class=${styles.empty}>${t('cp_home_no_custody')}</p>
    </section>`;
  }
  const data = home.data as CoparentingHomeData;
  const sorted = data.school.slice().sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  const onAddClick = (): void => {
    formState.type = 'note';
    formState.subject = '';
    formState.value = '';
    formState.teacher = '';
    formState.comment = '';
    openModal(MODAL_ADD);
  };
  const showModal = getModal() === MODAL_ADD;

  return html`
    <section class=${styles.view} data-testid="view-cp-school">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        dateLine: t('cp_school_dateline'),
        title: t('cp_school_hero_title'),
        accent: t('cp_school_hero_accent'),
      })}
      <button
        type="button"
        class=${styles.addBtn}
        data-testid="cp-school-add-btn"
        @click=${onAddClick}
      >
        ${t('cp_school_btn_add')}
      </button>
      ${sorted.length === 0
        ? html`<div class=${styles.empty} data-testid="cp-school-empty">
            <div class=${styles.emptyIcon}>🎓</div>
            <div class=${styles.emptyTitle}>${t('cp_school_empty_title')}</div>
            <div class=${styles.emptySub}>${t('cp_school_empty_sub')}</div>
          </div>`
        : html`<div class=${styles.list} data-testid="cp-school-list">
            ${sorted.map((e) => entryRowTemplate(e, home.id))}
          </div>`}
      ${showModal
        ? modalTemplate({
            title: t('cp_school_modal_title'),
            content: modalContentTemplate(home.id),
            onClose: closeModal,
            testid: 'cp-school-modal',
          })
        : html``}
    </section>
  `;
}
